import { Button, Mentions, Tooltip } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { EditOutlined, SendOutlined } from "@ant-design/icons";
import {
  createFilesMessagePayload,
  createMessagePayload,
  getChatMembers,
} from "../helper";
import { selectUser } from "../../../../../store/user/selectors";
import { sendWebSocketMessage } from "../../../../../store/chats/slice";
import { StyledChatInputWrapper } from "./styles";
import FileUploadInput from "./FileUploadInput";
import RewriteMessage from "./RewriteMessage";
import { getFullName } from "../../../../../utils/shared";
import EmojiPicker from "./EmojiPicker";
import {
  AI_ACTION_CONFIG,
  AI_ACTIONS,
} from "../../../../../utils/AiService/constant";
import { addEditSummary } from "../../../../../store/chats/thunks";
import { convertMentionsToIds } from "../../../../../components/shared/Mentions/helper";
import VoiceInput from "./VoiceInput";

const ChatInput = (props) => {
  const { onSendMessage, selectedChat, selectedTenantId } = props;
  const [message, setMessage] = useState("");
  const [selectedAction, setSelectedAction] = useState(null);
  const [summarizing, setSummarizing] = useState(false);

  const dispatch = useDispatch();

  const user = useSelector(selectUser);

  const users = useMemo(() => {
    return getChatMembers(selectedChat);
  }, [selectedChat]);

  const handleSendMessage = useCallback(
    (files) => {
      if (
        (!message?.trim() || !selectedChat?.id || !user?.user_id) &&
        !files?.length
      )
        return;

      const finalMessage = convertMentionsToIds(message, users);

      const payloadFunction = files?.length
        ? createFilesMessagePayload
        : createMessagePayload;

      dispatch(
        sendWebSocketMessage(
          payloadFunction({
            message: finalMessage,
            selectedChat,
            user,
            files,
          }),
        ),
      );

      onSendMessage();
      setMessage("");
    },
    [
      message,
      selectedChat?.id,
      selectedTenantId,
      user?.user_id,
      dispatch,
      users,
    ],
  );

  const onSummaries = useCallback(async () => {
    try {
      setSummarizing(true);
      await dispatch(
        addEditSummary({
          tenant_id: selectedTenantId,
          conversation_id: selectedChat?.id,
          generated_by_user_id: user?.user_id,
        }),
      );
    } catch (error) {
      console.log("🚀 ~ index.jsx: ~ ChatInput ~ error:", error);
    } finally {
      setSummarizing(false);
    }
  }, [dispatch, selectedTenantId, selectedChat?.id, user?.user_id]);

  return (
    <StyledChatInputWrapper>
      <div className="ai-tools hide-scrollbar">
        {Object.keys(AI_ACTION_CONFIG).map((key) => (
          <Button
            key={key}
            onClick={() => setSelectedAction(key)}
            size="small"
            disabled={!message.trim()}
          >
            <span>{AI_ACTION_CONFIG[key].icon}</span>
            {AI_ACTION_CONFIG[key].buttonLabel}
          </Button>
        ))}
        <Button
          size="small"
          onClick={onSummaries}
          disabled={summarizing}
          loading={summarizing}
        >
          <span>📝</span>
          Summaries
        </Button>
      </div>
      <div className="input-box">
        <div className="flex">
          <FileUploadInput handleSendMessage={handleSendMessage} />
          <EmojiPicker
            onEmojiClick={({ emoji }) => setMessage(message + emoji)}
          />
        </div>
        <Mentions
          value={message}
          onChange={(value) => setMessage(value)}
          placeholder="Send a message..."
          autoSize={{ minRows: 1, maxRows: 4 }}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          className="text-message-input"
          options={[
            ...users.map((u) => ({
              value: getFullName(u),
              label: getFullName(u),
            })),
          ]}
        />

        <Tooltip title="Format text professionally">
          <Button
            type="text"
            onClick={() => setSelectedAction(AI_ACTIONS.FORMAT_TEXT)}
            disabled={!message.trim()}
            icon={<EditOutlined />}
            shape="circle"
            className="edit-message-button icon-btn"
          />
        </Tooltip>

        <VoiceInput handleSendMessage={handleSendMessage} />
        <Button
          type="primary"
          onClick={handleSendMessage}
          shape="circle"
          disabled={!message?.trim()}
          icon={<SendOutlined />}
        />
        {selectedAction && (
          <RewriteMessage
            setMessage={(value) => {
              setMessage(value);
              setSelectedAction(null);
            }}
            originalMessage={message}
            onClose={() => setSelectedAction(null)}
            disabled={!message.trim()}
            messages={selectedChat?.messages}
            selectedAction={selectedAction}
          />
        )}
      </div>
    </StyledChatInputWrapper>
  );
};

export default ChatInput;
